import type {
  SubitemScanParent,
  SubitemScanResponse,
  SubitemScanRow,
  SubitemScanSubitem,
  SubitemState,
} from "./__fixtures__/discrepancyFixtures";
import type { ModalResult, SubitemResyncRequest } from "./SubitemResyncModal";

/**
 * Patches the cached subitem scan the moment a resync lands, so the row the user acted on
 * changes NOW rather than when the page's next full scan comes back.
 *
 * WHY (2026-09-10, account 9013250): `onResynced` used to be a bare `mutate()`, which starts a
 * fresh async scan of every parent on the page — 25 parents, 24 seconds in the log — and only
 * then replaces the data. The modal said "updated" while the row behind it kept showing the
 * drift it had just fixed. The Items tab already patches optimistically
 * (`patchDiscrepanciesAfterResync`); the Subitems tab now does the same, with the SAME
 * revalidation still running behind it, so the truth still lands and still wins.
 *
 * WHAT IT CLAIMS, AND ONLY THAT. This reads the engine's own answer, never a guess:
 *   - a board in `succeeded` takes the winner's value — the winner is the row's engine-computed
 *     `winningBoardId`, the same board the write copied FROM;
 *   - a board with a `failed` entry for this row (board-level, or naming this row's column) is
 *     left exactly as it was, and keeps the row drifting;
 *   - a "nothing to change" result (nothing succeeded, nothing failed) means the engine proved
 *     the rows already agree, so the row flips to `ok` and its cells take the winner's value;
 *   - a result carrying a whole-request rejection is NOT patched: a bulk request spans two
 *     endpoints and the modal cannot say which half the rejection covers, so the revalidation
 *     alone decides.
 * A `failed` entry whose reason is "no synced subitem on this board" is ignored: that board was
 * never a candidate (see simb-integration-ms's target-boards.ts), not a write that failed.
 *
 * The counts the badges read — the subitem's `state`/`columnsAffected`, the parent's
 * `subitemsAffected`/`totalDiscrepancies` — are recomputed from the patched rows with the
 * classifier's own rules (subitem-classifier.ts: name drift overrides value drift; a terminal
 * subitem counts once; a drifted one counts once per affected row).
 *
 * Pure. `now` is injected so a test can pin the stamp; production passes nothing.
 */
const NAME_COLUMN_KEY = "__name__";
const NOT_A_CANDIDATE = "no synced subitem on this board";

type ValueEntry = SubitemScanRow["valuesByBoard"][string];

/** Boards whose write was actually REFUSED for this row. */
const refusedBoardsFor = (row: SubitemScanRow, result: ModalResult): Set<string> =>
  new Set(
    result.failed
      .filter((failure) => failure.reason !== NOT_A_CANDIDATE)
      .filter((failure) => !failure.columnKey || failure.columnKey === row.columnKey)
      .map((failure) => failure.boardId),
  );

const patchRow = (row: SubitemScanRow, result: ModalResult, now: string): SubitemScanRow => {
  // No engine-chosen winner means no value to copy; leave the row for the revalidation.
  if (!row.winningBoardId) return row;

  const winner: ValueEntry = row.valuesByBoard[row.winningBoardId] ?? null;
  const landed = new Set(result.succeeded);
  const refused = refusedBoardsFor(row, result);
  const nothingToChange = result.succeeded.length === 0 && result.failed.length === 0;
  // A cleared winner clears the targets ("clear on N boards"); a value copies.
  const copied: ValueEntry = winner ? { ...winner, updatedAt: now } : null;

  const valuesByBoard = Object.fromEntries(
    Object.entries(row.valuesByBoard).map(([boardId, entry]) => {
      if (boardId === row.winningBoardId || refused.has(boardId)) return [boardId, entry];
      if (landed.has(boardId) || nothingToChange) return [boardId, copied];
      return [boardId, entry];
    }),
  );

  // Refusals only keep the row drifting when they name a board this subitem is actually on.
  const stillDrifting = [...refused].some((boardId) =>
    Object.prototype.hasOwnProperty.call(row.valuesByBoard, boardId),
  );

  return { ...row, valuesByBoard, state: stillDrifting ? row.state : "ok" };
};

const patchSubitem = (
  subitem: SubitemScanSubitem,
  requestedRows: SubitemScanRow[],
  result: ModalResult,
  now: string,
): SubitemScanSubitem => {
  const requested = new Set(requestedRows.map((row) => row.columnKey));
  const rows = subitem.rows.map((row) =>
    row.state === "ok" || !requested.has(row.columnKey) ? row : patchRow(row, result, now),
  );

  const nameRow = rows.find((row) => row.columnKey === NAME_COLUMN_KEY);
  const driftRows = rows.filter((row) => row.columnKey !== NAME_COLUMN_KEY && row.state !== "ok");
  const state: SubitemState =
    nameRow && nameRow.state !== "ok" ? "name_drift" : driftRows.length > 0 ? "value_drift" : "ok";

  // A name that just synced is the winner's name everywhere, so the header says so too.
  const winnerName =
    nameRow && nameRow.state === "ok" && nameRow.winningBoardId
      ? nameRow.valuesByBoard[nameRow.winningBoardId]?.text
      : undefined;

  return {
    ...subitem,
    rows,
    state,
    columnsAffected: driftRows.length,
    name: winnerName ?? subitem.name,
  };
};

const patchParent = (
  parent: SubitemScanParent,
  request: SubitemResyncRequest,
  result: ModalResult,
  now: string,
): SubitemScanParent => {
  const requestedRows = request.kind === "subitem" ? request.rows : [request.row];
  const subitems = parent.subitems.map((subitem) =>
    subitem.subitemSyncId === request.subitemSyncId
      ? patchSubitem(subitem, requestedRows, result, now)
      : subitem,
  );

  return {
    ...parent,
    subitems,
    subitemsAffected: subitems.filter((subitem) => subitem.state !== "ok").length,
    totalDiscrepancies: subitems.reduce((sum, subitem) => {
      if (subitem.state === "ok") return sum;
      const rowCount = subitem.rows.filter((row) => row.state !== "ok").length;
      return sum + (rowCount || 1);
    }, 0),
  };
};

export const patchSubitemScanAfterResync = (
  scan: SubitemScanResponse | undefined,
  request: SubitemResyncRequest,
  result: ModalResult,
  now: string = new Date().toISOString(),
): SubitemScanResponse | undefined => {
  // `undefined` is what SWR hands an updater when nothing is cached yet — and what it must
  // get back, since it commits whatever the updater returns (useSubitemDiscrepancy.tsx).
  if (!scan) return scan;
  if (result.rejected.length > 0) return scan;

  return {
    ...scan,
    parents: scan.parents.map((parent) =>
      parent.parentSyncId === request.parentSyncId ? patchParent(parent, request, result, now) : parent,
    ),
  };
};
